import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
  type InjectionKey,
} from 'vue'
import type { FlowContext, FlowResult } from '../../../../../shared/player-contracts/src'
import type { DeckCardInput, DeckElementInteraction } from '../../deck/types'
import type { DeckTemplateSettings } from '../../deck/templateSettings'
import { useI18n } from '../../i18n'
import { useUserSettingsStore } from '../../stores/useUserSettingsStore'
import { coerceFlowGraphV1, type FlowGraphV1 } from '../flowchart/flowGraph'
import { resolveShownMedia, type MediaMaterial } from '../flowchart/runtime/media'
import { buildPreStartPhases, phaseSegmentColors } from '../flowchart/runtime/preStart'
import { createFlowSessionState } from '../flowchart/runtime/sessionState'
import {
  DEBUG_BUILD_TAG,
  useFlowchartRunner,
  type FlowRunnerEmit,
} from '../flowchart/runtime/useFlowchartRunner'
import { useDeckPlayerStore } from '../store/useDeckPlayerStore'
import type { DeckPlayerModel, DeckPlayerShortcodeProps } from '../types'
import { useAudioPolicy } from './useAudioPolicy'
import { useConfetti } from './useConfetti'
import { useNowPlayingMedia } from './useNowPlayingMedia'
import { usePlayerPresentation } from './usePlayerPresentation'
import { usePlayerProgress } from './usePlayerProgress'
import { useStorageFlag } from './useStorageFlag'

/** Props of DeckPlayerShell (see the component for documentation). */
export type DeckPlayerShellProps = {
  deckId: string
  deckTitle: string
  model: DeckPlayerModel
  cards: DeckCardInput[]
  deckCardIds: string[]
  deckGroups: Array<{ id?: string; title: string; cardIds: string[] }>
  flowGraph: FlowGraphV1 | null
  debug: boolean
  restRoot?: string
  nonce?: string
  forceComplete: boolean
  allowReplayWhenCompleted: boolean
  retryAllowed: boolean
  suppressCompletionUi: boolean
  keepCardOnFlowEnd: boolean
  retryBlockedUntil: string | null
  flowPropagationBlockedUntil: string | null
  suppressProgressWrites: boolean
  deckTemplate: DeckTemplateSettings
  initialValues: Record<string, boolean[]>
  initialFlowPhaseId?: string | null
  playerProps: DeckPlayerShortcodeProps
  isLoggedIn: boolean
  manualStep?: boolean
  stepSignal?: number
  debugStepDelay?: number
  inEditor: boolean
  mediaMaterials?: MediaMaterial[]
  flowContext?: FlowContext | null
  onMediaPause?: (
    info: { displayMode: string; mediaType: string; mediaName: string; mediaId: string },
    resume: () => void,
  ) => void
}

export type DeckPlayerShellEmit = FlowRunnerEmit & {
  (e: 'retry', payload: { deckId: string }): void
  (e: 'completed', payload: { deckId: string }): void
  (e: 'flow-end', payload: { deckId: string; result: FlowResult }): void
  (e: 'flow-continue', payload: { deckId: string }): void
  (e: 'flow-reset', payload: { deckId: string }): void
  (e: 'awaiting-start'): void
}

/** Default "play all cards once" flow for the flowchart model without a configured graph. */
const DEFAULT_FLOW_GRAPH = {
  version: 1 as const,
  nodes: [
    { id: '_d_start', type: 'start', label: 'Start', position: { x: 120, y: 120 }, data: {} },
    {
      id: '_d_group',
      type: 'group',
      label: 'All Cards',
      position: { x: 400, y: 120 },
      data: { groupId: '__all__' },
    },
    {
      id: '_d_cond',
      type: 'condition',
      label: 'Done?',
      position: { x: 680, y: 120 },
      data: { condLeft: 'R1', condOp: '==', condRight: 'literal', condRightLit: 0 },
    },
    {
      id: '_d_pull',
      type: 'action',
      label: 'Pull Card',
      position: { x: 680, y: 300 },
      data: { actionType: 'pull-next-card' },
    },
    { id: '_d_end', type: 'end', label: 'End', position: { x: 960, y: 120 }, data: {} },
  ],
  edges: [
    { id: '_e1', fromNodeId: '_d_start', fromPort: 'next', toNodeId: '_d_group', toPort: 'in' },
    { id: '_e2', fromNodeId: '_d_group', fromPort: 'next', toNodeId: '_d_cond', toPort: 'in-a' },
    { id: '_e3', fromNodeId: '_d_cond', fromPort: 'true', toNodeId: '_d_end', toPort: 'in-a' },
    { id: '_e4', fromNodeId: '_d_cond', fromPort: 'else', toNodeId: '_d_pull', toPort: 'in' },
    { id: '_e5', fromNodeId: '_d_pull', fromPort: 'next', toNodeId: '_d_cond', toPort: 'in-b' },
  ],
}

/**
 * Builds a DeckPlayerShell: progress model (checkbox queue or flowchart runtime),
 * presentation, media playback and the admin testing aids. Composables are created in
 * the order the original component registered its watchers and hooks.
 */
export function useDeckPlayerShell(props: DeckPlayerShellProps, emit: DeckPlayerShellEmit) {
  const store = useDeckPlayerStore()
  const { t } = useI18n()
  const userSettings = useUserSettingsStore()

  const debugLog = (label: string, data?: unknown) => {
    if (props.debug !== true) return
    try {
      console.log(`[DeckPlayer] ${label}`, data ?? '')
    } catch {
      // ignore
    }
  }
  const debugWarn = (label: string, data?: unknown) => {
    if (props.debug !== true) return
    try {
      console.warn(`[DeckPlayer] ${label}`, data ?? '')
    } catch {
      // ignore
    }
  }

  const graph = computed(() =>
    props.model === 'flowchart' ? coerceFlowGraphV1(props.flowGraph ?? DEFAULT_FLOW_GRAPH) : null,
  )
  const deckCardIds = computed(() =>
    Array.isArray(props.deckCardIds) && props.deckCardIds.length
      ? props.deckCardIds.slice()
      : (props.cards ?? []).map((c) => c.id).filter(Boolean),
  )

  const state = createFlowSessionState()
  const playerEl = ref<HTMLElement | null>(null)
  const fireConfetti = useConfetti(playerEl)

  const resolvedMedia = computed(() =>
    state.showingMedia.value
      ? resolveShownMedia(state.showingMedia.value, props.mediaMaterials ?? [])
      : null,
  )
  const nowPlaying = useNowPlayingMedia(props, state, resolvedMedia)

  // Presentation is created after the runner (its resize hook was registered last in
  // the original component); the runner only reads it lazily, after setup.
  const late = {} as { presentation: ReturnType<typeof usePlayerPresentation> }
  const runner = useFlowchartRunner({
    props,
    emit,
    state,
    graph,
    deckCardIds,
    playerEl,
    displayedCard: () => late.presentation.patchedCard.value,
    cardChangeAnimation: () => late.presentation.ui.value.cardChangeAnimation,
    fireConfetti,
    onMediaShown: nowPlaying.reset,
  })

  // Admin testing aids (localStorage flags)
  onMounted(() => {
    // eslint-disable-next-line no-console
    console.warn('[PFC-DBG] DeckPlayerShell mounted', {
      build: DEBUG_BUILD_TAG,
      model: props.model,
      inEditor: props.inEditor,
      deckId: props.deckId,
    })
  })
  const debugFlag = useStorageFlag('perlingo.debug', 'perlingo:debug-toggle', (value) => {
    // eslint-disable-next-line no-console
    console.warn('[PFC-DBG] readDebugActive', {
      value,
      model: props.model,
      inEditor: props.inEditor,
    })
  })

  // First load outside the editor waits behind the pre-start screen, so the Start
  // gesture unlocks browser autoplay before play-audio nodes call play().
  let autoStartFired = false
  watch(
    () => [props.deckId, props.model, props.flowGraph, props.cards, props.deckGroups] as const,
    () => {
      if (props.model !== 'flowchart' || props.forceComplete) return
      if (!autoStartFired && !props.inEditor) {
        autoStartFired = true
        state.awaitingStart.value = true
        return
      }
      runner.start()
    },
    { immediate: true },
  )
  const startFlow = () => {
    state.awaitingStart.value = false
    runner.start()
  }

  // Programmatic play() needs a user gesture in some browsers (Firefox): unlock audio
  // on the first pointer interaction with the player.
  const unlockAudioOnce = () => {
    playerEl.value?.removeEventListener('pointerdown', unlockAudioOnce, true)
    try {
      const Ctx =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctx) return
      const ctx = new Ctx()
      void ctx.resume().then(() => setTimeout(() => void ctx.close(), 500))
    } catch {
      // Audio unlocking is best effort.
    }
  }
  onMounted(() => {
    playerEl.value?.addEventListener('pointerdown', unlockAudioOnce, true)
    // Let the host show its own Start button.
    if (state.awaitingStart.value) emit('awaiting-start')
  })
  onBeforeUnmount(() => {
    playerEl.value?.removeEventListener('pointerdown', unlockAudioOnce, true)
  })

  const canvasFlag = useStorageFlag('perlingo.flow-canvas', 'perlingo:canvas-toggle', (value) => {
    // eslint-disable-next-line no-console
    console.warn('[PFC-DBG] readCanvasActive', { value, model: props.model })
  })
  const skipFlag = useStorageFlag(
    'perlingo.skip-button',
    'perlingo:skip-button-toggle',
    (value) => {
      // eslint-disable-next-line no-console
      console.warn('[PFC-DBG] readSkipButtonActive', { value, model: props.model })
    },
  )
  // Opening/closing the debug panel deliberately does not pause/resume the flow:
  // resuming fired a synthesized continuation that could fast-forward to the end.
  const toolVisible = (active: boolean) => !props.inEditor && active && props.model === 'flowchart'
  const showDebugPanel = computed(() => toolVisible(debugFlag.active.value))
  const showFlowCanvas = computed(() => toolVisible(canvasFlag.active.value))
  const showSkipButton = computed(() => toolVisible(skipFlag.active.value))

  // Progress, presentation and audio
  const progress = usePlayerProgress({
    props,
    emit,
    store,
    flow: state,
    t,
    fireConfetti,
    hasValidFlowGraph: () => Boolean(graph.value),
    primaryRef: () => late.presentation.primaryRef.value,
    debugLog,
    debugWarn,
  })

  const presentation = usePlayerPresentation({
    props,
    forceDarkMode: () => userSettings.forceDarkMode,
    lastAdvanceOutcome: (deckId) => store.lastAdvanceOutcome[deckId] ?? null,
    flowTransition: () => state.cardTransition.value,
    flowShowBackFirst: () => state.showBackFirst.value,
    awaitingStart: () => state.awaitingStart.value,
    activeCard: progress.activeCard,
    activeValues: progress.activeValues,
  })
  late.presentation = presentation

  useAudioPolicy(props, progress.activeCard, () => userSettings.autoplayAudio === true)

  // Pre-start screen
  const rewardPoints = computed(() => {
    const p = props.playerProps?.points
    return props.playerProps?.showPoints === false || typeof p !== 'number' ? null : p
  })
  const rewardPearls = computed(() => {
    const p = props.playerProps?.pearls
    return props.playerProps?.showPearls === false || typeof p !== 'number' ? null : p
  })
  const rewardsVisible = computed(() => rewardPearls.value !== null || rewardPoints.value !== null)

  const preStartPhases = computed(() => {
    if (!state.awaitingStart.value || !graph.value) return []
    return buildPreStartPhases(graph.value, {
      deckGroups: props.deckGroups ?? [],
      allCardIds: props.cards.map((c) => c.id),
      initialValues: props.initialValues ?? {},
      countCheckboxes: runner.countCheckboxes,
    })
  })
  // The card-sized outer box plus a scale variable for the content.
  const preStartStyle = computed(() => ({
    ...presentation.previewScaledOuterStyle.value,
    '--pre-start-scale': String(Math.max(0.1, presentation.previewEffectiveScale.value)),
  }))

  // Flow controls
  const handleInteraction = (interaction: DeckElementInteraction) => {
    if (props.model === 'flowchart') {
      runner.handleFlowInteraction(interaction)
      return
    }
    progress.handleCheckboxInteraction(interaction)
  }

  const handleFlowContinue = () => {
    if (progress.isPhaseInterlude.value) {
      state.phaseResume.value?.()
      return
    }
    emit('flow-continue', { deckId: props.deckId })
  }

  const resetConfirmPending = ref(false)
  let resetConfirmTimer: ReturnType<typeof setTimeout> | null = null
  /** Reset needs a second click within 3 s. */
  const handleResetClick = () => {
    if (!resetConfirmPending.value) {
      resetConfirmPending.value = true
      if (resetConfirmTimer !== null) clearTimeout(resetConfirmTimer)
      resetConfirmTimer = setTimeout(() => {
        resetConfirmPending.value = false
      }, 3000)
      return
    }
    if (resetConfirmTimer !== null) {
      clearTimeout(resetConfirmTimer)
      resetConfirmTimer = null
    }
    resetConfirmPending.value = false
    emit('flow-reset', { deckId: props.deckId })
  }

  const player = {
    props,
    t,
    state,
    graph,
    runner,
    nowPlaying,
    resolvedMedia,
    progress,
    presentation,
    playerEl,
    rewardPoints,
    rewardPearls,
    rewardsVisible,
    preStartPhases,
    preStartStyle,
    phaseSegmentColors,
    showDebugPanel,
    showFlowCanvas,
    showSkipButton,
    closeDebugPanel: debugFlag.clear,
    closeFlowCanvas: canvasFlag.clear,
    startFlow,
    handleInteraction,
    handleFlowContinue,
    handleResetClick,
    resetConfirmPending,
    propagationBlockedText: computed(() => t('deckPlayer.shell.flowPropagationBlocked')),
  }
  provide(DECK_PLAYER_KEY, player)
  return player
}

export type DeckPlayerShellState = ReturnType<typeof useDeckPlayerShell>

const DECK_PLAYER_KEY: InjectionKey<DeckPlayerShellState> = Symbol('deck-player-shell')

/** The enclosing DeckPlayerShell. */
export function useDeckPlayerContext(): DeckPlayerShellState {
  const player = inject(DECK_PLAYER_KEY)
  if (!player) throw new Error('Deck player components must be used inside DeckPlayerShell')
  return player
}
